import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { withFragments } from '@sorare/core/src/lib/gql';

import { RuleHelperFnReturnType } from '../types';
import { getNationalitiesRule_so5Rules } from './__generated__/getNationalitiesRule.graphql';

export const getNationalitiesRule = withFragments(
  (
    rules: getNationalitiesRule_so5Rules,
    error: string
  ): RuleHelperFnReturnType => {
    const rule = rules.nationalitiesCount;
    if (!rule) {
      return null;
    }

    let display = 'global';
    if (rule.min && rule.max) {
      display = 'between';
    } else if (rule.min) {
      display = 'min';
    } else if (rule.max) {
      display = 'max';
    }

    return {
      id: 'nationalities',
      label: (
        <FormattedMessage
          id="Rules.nationalitiesCount.label"
          defaultMessage="{display, select,
            between {Between {min} and {max}}
            min {Min {min}}
            max {Max {max}}
            other {Only}
          } {max, plural, one {player} other {players}} from"
          values={{
            display,
            min: rule.min,
            max: rule.max,
          }}
        />
      ),
      description: (
        <Vertical as="span" gap={0}>
          {rule.countries.map(({ slug, name }) => (
            <Horizontal key={slug} as="span">
              <img
                src={`${FRONTEND_ASSET_HOST}/flags/${slug}.svg`}
                alt=""
                width="16"
                height="16"
              />
              <span>{name}</span>
            </Horizontal>
          ))}
        </Vertical>
      ),
      icon: (
        <svg
          width="16"
          height="13"
          viewBox="0 0 16 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 2.5C8 3.88071 6.88071 5 5.5 5C4.11929 5 3 3.88071 3 2.5C3 1.11929 4.11929 0 5.5 0C6.88071 0 8 1.11929 8 2.5Z"
            fill="white"
          />
          <path d="M3 7C1.34315 7 0 8.34315 0 10V13H8V7H3Z" fill="white" />
          <path d="M13 7H10V13H16V10C16 8.34315 14.6569 7 13 7Z" fill="white" />
          <path
            d="M12 5C13.1046 5 14 4.10457 14 3C14 1.89543 13.1046 1 12 1C10.8954 1 10 1.89543 10 3C10 4.10457 10.8954 5 12 5Z"
            fill="white"
          />
        </svg>
      ),
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getNationalitiesRule_so5Rules on So5Rules {
        id
        nationalitiesCount {
          countries {
            slug
            name
          }
          min
          max
        }
      }
    ` as TypedDocumentNode<getNationalitiesRule_so5Rules>,
  }
);
